import { useEffect, useCallback } from "react";

interface PhotoLightboxProps {
  images: string[];
  index: number;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
}

export default function PhotoLightbox({
  images,
  index,
  onClose,
  onIndexChange,
}: PhotoLightboxProps) {
  const count = images.length;
  const current = images[index];

  const go = useCallback(
    (next: number) => {
      if (count === 0) return;
      const normalized = (next + count) % count;
      onIndexChange?.(normalized);
    },
    [count, onIndexChange],
  );

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") go(index - 1);
      if (event.key === "ArrowRight") go(index + 1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [index, go, onClose]);

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/90"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-5 top-5 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-background-50/15 text-background-50 transition-colors hover:bg-background-50/25"
        aria-label="关闭大图"
      >
        <i className="ri-close-line text-2xl"></i>
      </button>

      {count > 1 && (
        <span className="absolute left-1/2 top-5 -translate-x-1/2 rounded-full bg-background-50/15 px-4 py-1.5 text-xs font-medium text-background-50">
          {index + 1} / {count}
        </span>
      )}

      {count > 1 && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            go(index - 1);
          }}
          className="absolute left-4 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-background-50/15 text-background-50 transition-colors hover:bg-background-50/25 md:left-8"
          aria-label="上一张"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
      )}

      <img
        src={current}
        alt={`会员照片 ${index + 1}`}
        className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain"
        onClick={(event) => event.stopPropagation()}
      />

      {count > 1 && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            go(index + 1);
          }}
          className="absolute right-4 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-background-50/15 text-background-50 transition-colors hover:bg-background-50/25 md:right-8"
          aria-label="下一张"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>
      )}
    </div>
  );
}